import { useRouterState } from "@tanstack/react-router";
import {
  UpdateLastVisitedLocation,
  updateLastVisitedLocationQueryString,
} from "@/data-access/user/queries/me-queries";
import { useDebouncedPersistWithKeepaliveFlush } from "@/hooks/useDebouncedPersistWithKeepaliveFlush";
import {
  formatLastVisitedLocation,
  shouldPersistLastVisitedLocation,
} from "@/lib/auth/authLoginUtils";
import { client } from "@/lib/urqlProvider";
import useAuth from "@/store/auth.slice";

/**
 * Watches authenticated route changes and persists pathname + search to the backend.
 * Flushes pending writes when the tab is hidden.
 */
export const useLastVisitedLocationSync = () => {
  const loggedIn = useAuth((state) => state.loggedIn);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });

  useDebouncedPersistWithKeepaliveFlush({
    loggedIn,
    scheduleDeps: [pathname, searchStr],
    resolvePending: () => {
      const location = formatLastVisitedLocation(pathname, searchStr);
      return shouldPersistLastVisitedLocation(location) ? location : null;
    },
    persistKey: (location) => location,
    isAlreadyCached: (location) =>
      useAuth.getState().user.last_visited_location === location,
    persist: async (location) => {
      const result = await client
        .mutation(UpdateLastVisitedLocation, { lastVisitedLocation: location })
        .toPromise();

      if (result.error) {
        console.warn("Failed to persist last visited location:", result.error);
        throw result.error;
      }

      const updated =
        result.data?.updateLastVisitedLocation?.lastVisitedLocation;
      if (!updated) return;

      const { user } = useAuth.getState();
      if (user.last_visited_location === updated) return;
      useAuth.setState({
        user: { ...user, last_visited_location: updated },
      });
    },
    shouldFlush: shouldPersistLastVisitedLocation,
    flushQueryString: updateLastVisitedLocationQueryString,
    flushVariables: (location) => ({ lastVisitedLocation: location }),
    onKeepaliveFlushSuccess: (location) => {
      const { user } = useAuth.getState();
      if (user.last_visited_location === location) return;
      useAuth.setState({
        user: { ...user, last_visited_location: location },
      });
    },
    flushErrorMessage: "Failed to flush last visited location:",
  });
};
